<template>
  <div>
    <page-header :breadcrumb="breadcrumb" :container-class="containerClass" />
    <div class="page-section border-bottom-2">
      <div :class="containerClass">
        <div class="row" v-if="isLoading">
          <div class="col-md-4 col-6" v-for="i in [1, 2, 3]" :key="i">
            <b-card>
              <b-skeleton class="mb-2" width="25px" height="25px"></b-skeleton>
              <b-skeleton width="25%" class="mb-2"></b-skeleton>
              <b-skeleton animation="wave" width="85%"></b-skeleton>
              <b-skeleton animation="wave" width="75%"></b-skeleton>
              <b-skeleton animation="wave" width="80%"></b-skeleton>
            </b-card>
          </div>
        </div>

        <div class="row" v-else>
          <div class="col-md-4 col-sm-6" v-for="template in allTemplates" :key="template.id">
            <div class="card p-3" style="height: 95%" :id="template.id">
              <div class="ml-0">
                <img
                  style="object-fit: contain"
                  class="mb-2"
                  v-if="template.image"
                  :src="getImage(template.image)"
                  width="45"
                  height="45"
                />
                <md-icon v-else style="font-size: 40px" class="mb-2 text-primary">description</md-icon>
              </div>
              <div class="mb-2">
                <strong> {{ template.title }} </strong>
              </div>
              <b-card-text>{{ template.description }}</b-card-text>
              <div class="mt-auto">
                <b-btn
                  class="w-100"
                  style="height: 40px"
                  :variant="template.enabled ? 'success' : template.variant"
                  @click.prevent="template.btnAction()"
                  :disabled="template.disabled"
                >
                  <i v-if="template.isLoading" class="fas fa-circle-notch fa-spin"></i>
                  <span v-else>
                    {{ template.btnTitle }}
                  </span>
                </b-btn>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <b-modal
      v-model="showGhlModal"
      :title="ghlIntegrationId ? 'GoHighLevel Integration' : 'Setup GoHighLevel Integration'"
      size="md"
      centered
      @hidden="resetGhlForm"
    >
      <b-form @submit.prevent="saveGhlIntegration">
        <b-form-group label-for="ghl-pit">
          <template #label>
            PIT (Personal Integration Token)
            <md-icon
              id="pit-info-icon"
              class="text-muted ml-1"
              style="font-size: 18px; cursor: pointer; vertical-align: middle"
            >
              info
            </md-icon>
            <b-tooltip target="pit-info-icon" triggers="hover">
              Find this in GoHighLevel under Settings > API > Personal Integration Token
            </b-tooltip>
          </template>
          <b-form-input
            id="ghl-pit"
            v-model="ghlForm.pit"
            type="password"
            :placeholder="pitPlaceholder"
            :required="!ghlIntegrationId"
            autocomplete="new-password"
            :class="{ 'pit-masked-input': isPitMaskedDisplay }"
          ></b-form-input>
        </b-form-group>

        <b-form-group label-for="ghl-location">
          <template #label>
            Location ID
            <md-icon
              id="location-info-icon"
              class="text-muted ml-1"
              style="font-size: 18px; cursor: pointer; vertical-align: middle"
            >
              info
            </md-icon>
            <b-tooltip target="location-info-icon" triggers="hover">
              Find this in GoHighLevel under Settings > Business Profile > Location ID
            </b-tooltip>
          </template>
          <b-form-input
            id="ghl-location"
            v-model="ghlForm.location_id"
            placeholder="Enter your Location ID"
            required
          ></b-form-input>
        </b-form-group>

        <b-form-group>
          <b-form-checkbox v-model="ghlForm.enabled" switch>
            Enable Integration
          </b-form-checkbox>
        </b-form-group>
      </b-form>

      <hr class="my-4" />

      <div class="mb-3">
        <h6 class="font-weight-bold mb-3">Supported Triggers</h6>
        <p class="text-muted small mb-3">
          The following events from TransitionEnroll and Transition will automatically trigger actions in your GoHighLevel CRM:
        </p>
        <div class="table-responsive supported-triggers-table">
          <table class="table table-sm table-bordered">
            <thead class="thead-light">
              <tr>
                <th>Event</th>
                <th>Source</th>
                <th>Action in CRM</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Program inquiry submitted</td>
                <td><span class="badge badge-light">TransitionEnroll</span></td>
                <td>Creates lead → <strong>New Inquiry</strong></td>
              </tr>
              <tr>
                <td>Scholarship inquiry</td>
                <td><span class="badge badge-light">TransitionEnroll</span></td>
                <td>Creates lead → <strong>New Inquiry</strong></td>
              </tr>
              <tr>
                <td>Pre-enrollment submitted</td>
                <td><span class="badge badge-light">TransitionEnroll</span></td>
                <td>Creates lead → <strong>Application Started</strong></td>
              </tr>
              <tr>
                <td>Pre-enrollment approved</td>
                <td><span class="badge badge-light">TransitionEnroll</span></td>
                <td>Updates lead → <strong>Approved</strong></td>
              </tr>
              <tr>
                <td>Student registered</td>
                <td><span class="badge badge-light">TransitionEnroll</span></td>
                <td>Creates/Updates lead → <strong>Converted to Student</strong></td>
              </tr>
              <tr>
                <td>Payment plan past due</td>
                <td><span class="badge badge-light">TransitionEnroll</span></td>
                <td>Updates student → <strong>Past Due</strong></td>
              </tr>
              <tr>
                <td>Enrollment dropped</td>
                <td><span class="badge badge-light">TransitionEnroll</span></td>
                <td>Updates student → <strong>Withdrawn</strong></td>
              </tr>
              <tr>
                <td>Student account created</td>
                <td><span class="badge badge-light">Transition</span></td>
                <td>Creates <strong>student record</strong></td>
              </tr>
              <tr>
                <td>Student hired by employer</td>
                <td><span class="badge badge-light">Transition</span></td>
                <td>Updates student → <strong>Placed</strong></td>
              </tr>
              <tr>
                <td>Profile completed</td>
                <td><span class="badge badge-light">Transition</span></td>
                <td><strong>Activity logged</strong> on student</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <template #modal-footer>
        <div class="w-100 d-flex justify-content-end">
          <b-button
            variant="primary"
            @click="saveGhlIntegration"
            :disabled="isGhlSaving"
          >
            <i v-if="isGhlSaving" class="fas fa-circle-notch fa-spin mr-1"></i>
            {{ isGhlSaving ? 'Saving...' : 'Save' }}
          </b-button>
        </div>
      </template>
    </b-modal>
  </div>
</template>

<script>
import { get } from 'lodash';
import PageHeader from '@/components/Ui/PageHeader.vue';
import { mapActions, mapGetters } from 'vuex';
import Page from '@/components/Page.vue';
var UsaStates = require('usa-states').UsaStates;
import generator from 'generate-password';

export default {
  components: { PageHeader },
  extends: Page,

  data() {
    return {
      title: 'Integrations',
      isLoading: false,
      isFacetLinkLoading: false,
      isFacetLinked: false,
      isGhlLoading: false,
      isGhlIntegrated: false,
      showGhlModal: false,
      isGhlSaving: false,
      ghlIntegrationId: null,
      ghlForm: {
        pit: '',
        location_id: '',
        enabled: true,
      },
      existingPitMask: '',
    };
  },

  computed: {
    ...mapGetters('auth', ['getLoggedInUser']),
    ...mapGetters('school', ['getIsFacetsLinked', 'getGhlIntegration']),
    breadcrumb() {
      return [
        { text: this.$t('home'), to: this.routes.home },
        { text: 'Integrations', active: true },
      ];
    },
    pitPlaceholder() {
      if (this.ghlIntegrationId && this.existingPitMask) {
        return `${this.existingPitMask}`;
      }
      return 'Enter your PIT token';
    },
    isPitMaskedDisplay() {
      return !!(this.ghlIntegrationId && this.existingPitMask && !this.ghlForm.pit);
    },
    allTemplates() {
      return [
        {
          id: 1,
          image: 'integration7.webp',
          title: 'GoHighLevel',
          description:
            'Connect GoHighLevel to streamline your outreach and automate key workflows, creating a seamless bridge between your CRM and student engagement.',
          btnTitle: this.ghlIntegrationId ? (this.isGhlIntegrated ? 'Integrated (Enabled)' : 'Integrated (Disabled)') : 'Integrate',
          btnAction: this.openGhlModal,
          isLoading: this.isGhlLoading,
          enabled: this.ghlIntegrationId && this.isGhlIntegrated,
          disabled: false,
          variant: this.ghlIntegrationId ? (this.isGhlIntegrated ? 'success' : 'warning') : 'primary',
        },
        {
          id: 2,
          image: 'integration1.webp',
          title: 'FACETS Healthcare',
          description:
            'With FACETS, experience seamless syncing of online learning and easy management of healthcare-focused course content, quizzes, and assignments.',
          btnTitle: 'Visit Site',
          btnAction: this.openFacetsSite,
          isLoading: this.isFacetLinkLoading,
          enabled: this.isFacetLinked,
          disabled: false,
          variant: 'primary',
        },
        {
          id: 3,
          image: 'integration2.png',
          title: 'Mailchimp',
          description: `Combine Mailchimp's powerful email marketing with your student data for more effective, targeted communication.`,
          btnTitle: 'Coming Soon',
          btnAction: null,
          isLoading: false,
          enabled: false,
          disabled: true,
          variant: 'light',
        },
        {
          id: 4,
          image: 'integration3.png',
          title: 'Google Sheets',
          description:
            'Connect with Google Sheets for effortless student data synchronization. Collaborate on spreadsheets in real-time.',
          btnTitle: 'Coming Soon',
          btnAction: null,
          isLoading: false,
          enabled: false,
          disabled: true,
          variant: 'light',
        },
        {
          id: 5,
          image: 'integration4.png',
          title: 'TalentLMS',
          description:
            'Integrate TalentLMS to sync your online learning management system, fostering growth and learning in a streamlined manner.',
          btnTitle: 'Coming Soon',
          btnAction: null,
          isLoading: false,
          enabled: false,
          disabled: true,
          variant: 'light',
        },
        {
          id: 6,
          image: 'integration5.png',
          title: 'Hubspot',
          description: `Sync your student data with Hubspot. Benefit from real-time integration within your dashboard.`,
          btnTitle: 'Coming Soon',
          btnAction: null,
          isLoading: false,
          enabled: false,
          disabled: true,
          variant: 'light',
        },
        {
          id: 7,
          image: 'integration6.jpeg',
          title: 'Moodle',
          description:
            'Sync student data with Moodle and manage secure, engaging eLearning experiences directly within your system.',
          btnTitle: 'Coming Soon',
          btnAction: null,
          isLoading: false,
          enabled: false,
          disabled: true,
          variant: 'light',
        },
      ];
    },
  },
  watch: {
    getIsFacetsLinked: {
      immediate: true,
      deep: true,
      handler(value) {
        if (value) {
          this.isFacetLinked = this.getIsFacetsLinked;
        }
      },
    },
    getGhlIntegration: {
      immediate: true,
      deep: true,
      handler(value) {
        if (value) {
          this.isGhlIntegrated = value.enabled;
          this.ghlIntegrationId = value.id || null;
        } else {
          this.isGhlIntegrated = false;
          this.ghlIntegrationId = null;
        }
      },
    },
  },
  methods: {
    ...mapActions('school', ['linkFacets', 'getFacetsVerification', 'createGhlIntegration', 'updateGhlIntegration']),
    ...mapActions('school', { fetchGhlIntegrationData: 'getGhlIntegration' }),
    get,
    getImage(image) {
      return require(`@/assets/images/integrations/${image}`);
    },
    stateAbbreviation(name) {
      var usStates = new UsaStates();
      return get(usStates.states.filter((state) => state.name === name)[0], 'abbreviation');
    },
    async fetchFacetVerification() {
      await this.getFacetsVerification();
    },
    async fetchGhlIntegration() {
      this.isGhlLoading = true;
      try {
        await this.fetchGhlIntegrationData({ school: this.getLoggedInUser.linked_entity.id });
      } catch (error) {
        if (error.response && error.response.status !== 404) {
          this.makeToast({ variant: 'danger', msg: 'Failed to fetch GHL integration status.' });
        }
      }
      this.isGhlLoading = false;
    },
    maskPit(pit) {
      if (!pit) return '';
      if (pit.length <= 8) return '********';
      return `${pit.slice(0, 4)}****${pit.slice(-4)}`;
    },
    openGhlModal() {
      if (this.getGhlIntegration) {
        this.existingPitMask = this.maskPit(this.getGhlIntegration.pit);
        this.ghlForm.pit = '';
        this.ghlForm.location_id = this.getGhlIntegration.location_id;
        this.ghlForm.enabled = this.getGhlIntegration.enabled;
      } else {
        this.existingPitMask = '';
      }
      this.showGhlModal = true;
    },
    resetGhlForm() {
      if (!this.getGhlIntegration) {
        this.ghlForm = {
          pit: '',
          location_id: '',
          enabled: true,
        };
        this.existingPitMask = '';
      } else {
        this.existingPitMask = this.maskPit(this.getGhlIntegration.pit);
        this.ghlForm.pit = '';
        this.ghlForm.location_id = this.getGhlIntegration.location_id;
        this.ghlForm.enabled = this.getGhlIntegration.enabled;
      }
    },
    async saveGhlIntegration() {
      this.isGhlSaving = true;
      try {
        const trimmedPit = this.ghlForm.pit.trim();
        if (this.ghlIntegrationId) {
          const data = {
            location_id: this.ghlForm.location_id,
            enabled: this.ghlForm.enabled,
          };
          if (trimmedPit) {
            data.pit = trimmedPit;
          }
          await this.updateGhlIntegration({
            id: this.ghlIntegrationId,
            data,
          });
          this.makeToast({ variant: 'success', msg: 'GHL Integration saved successfully.' });
        } else {
          await this.createGhlIntegration({
            school: this.getLoggedInUser.linked_entity.id,
            ...this.ghlForm,
            pit: trimmedPit,
          });
          this.makeToast({ variant: 'success', msg: 'GHL Integration created successfully.' });
        }
        this.showGhlModal = false;
        await this.fetchGhlIntegration();
      } catch (error) {
        const errorMsg = get(error, 'response.data.message', 'Failed to save GHL Integration.');
        this.makeToast({ variant: 'danger', msg: errorMsg });
      }
      this.isGhlSaving = false;
    },
    async linkToFacet() {
      this.isFacetLinkLoading = true;

      const data = {
        state_abbr: this.stateAbbreviation(this.getLoggedInUser.linked_entity.state),
        password: generator.generate({ length: 10, numbers: true, strict: true }),
      };
      try {
        await this.linkFacets(data);

        this.isFacetLinked = this.getIsFacetsLinked;
        if (this.isFacetLinked) {
          this.makeToast({ variant: 'success', msg: 'Your Account Linked Successfully.' });
        } else {
          this.isFacetLinked = false;
          this.makeToast({ variant: 'danger', msg: 'Unable To Link Please Try Again Later.' });
        }
      } catch (error) {
        //
      }

      this.isFacetLinkLoading = false;
    },
    openFacetsSite() {
      window.open('https://facetshealthcare.com/', '_blank', 'noopener,noreferrer');
    },
  },

  async mounted() {
    await this.fetchGhlIntegration();
  },
};
</script>

<style scoped>
.pit-masked-input {
  background-color: #fff7e6;
  border-color: #f0ad4e;
}

.pit-masked-input::placeholder {
  color: #495057;
  opacity: 1;
  font-weight: 600;
  letter-spacing: 0.2px;
}

.pit-masked-input:focus {
  border-color: #e59b22;
  box-shadow: 0 0 0 0.2rem rgba(240, 173, 78, 0.25);
}

.supported-triggers-table {
  max-height: 260px;
  overflow-y: auto;
}
</style>
